import { Box, FormControl, MenuItem, Select } from "@mui/material";
import { LiveStreamPlayer } from "components/scrapEyeDataDashboard/VideoPlayer/components/LiveStreamPlayer";
import { HlsVideoProps } from "hooks/useHlsVideo";
import { useMemo } from "react";
import { useVideoPlayerGroupContext } from "../contexts/VideoPlayerGroupContext";
import { HlsAngleVideoPlayer } from "./HlsVideoPlayer";

type SelectableAngleVideoPlayerProps = {
  location: string;
  groupId: number;
  startIso: string | null;
  endIso: string | null;
  isLive?: boolean;
  videoIndex?: number;
} & Omit<HlsVideoProps, "srcUrl" | "playbackRate" | "autoPlay">;

/**
 * Video player for an angle group at a location.
 * Users can select which angle to view via dropdown.
 * Uses priority-based automatic camera selection.
 * Angles can also be cycled via keyboard (Q key).
 */
export const SelectableAngleVideoPlayer = ({
  location,
  groupId,
  startIso,
  endIso,
  videoIndex = 0,
  isLive = false,
  ...props
}: SelectableAngleVideoPlayerProps) => {
  const { videoPlayerGroups, currentAngleIndices, setAngleIndexForGroup } =
    useVideoPlayerGroupContext();

  const group = useMemo(
    () => videoPlayerGroups.find((g) => g.id === groupId),
    [videoPlayerGroups, groupId]
  );

  const currentIndex = currentAngleIndices[groupId] ?? 0;
  const currentAngle = group?.angles[currentIndex];

  if (!group || !currentAngle) {
    return null;
  }

  return (
    <Box sx={{ position: "relative", width: "100%", minWidth: 0 }}>
      {group.angles.length > 1 && (
        <FormControl
          size="small"
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            zIndex: 10,
            minWidth: 120,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            borderRadius: 1,
          }}
        >
          <Select
            value={currentIndex}
            onChange={(e) => {
              const value = e.target.value;
              if (typeof value === "number") {
                setAngleIndexForGroup(groupId, value);
              }
            }}
            sx={{
              color: "white",
              fontSize: "0.75rem",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              "& .MuiSelect-icon": { color: "white" },
            }}
          >
            {group.angles.map((angle, index) => (
              <MenuItem key={angle.id} value={index}>
                {angle.name} ({angle.location_name})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {isLive ? (
        <LiveStreamPlayer
          location={location}
          angle={currentAngle.name}
          videoIndex={videoIndex}
        />
      ) : (
        <HlsAngleVideoPlayer
          location={location}
          angle={currentAngle.name}
          startIso={startIso}
          endIso={endIso}
          videoIndex={videoIndex}
          {...props}
        />
      )}
    </Box>
  );
};
